import type { Experiment } from 'types';
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useOrganizations } from '../useOrganizations';
import { useWebsites } from '../useWebsites';
import { toast } from '@/hooks/use-toast';
import { config } from 'config';

export const useExperiment = (experimentId: number) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const ganClient = useClient(`${config.GAN_URL}/google-analytics`);
  const [isStarting, setIsStarting] = useState(false);
  const [enforcing, setEnforcing] = useState(false);
  const { organizations, setOrganization } = useOrganizations();
  const { website, setWebsite } = useWebsites();

  const { data: experiment } = useQuery<Experiment>({
    enabled: !!experimentId,
    queryKey: ['EXPERIMENT', experimentId],
    queryFn: () => {
      return apiClient.get<Experiment>(`/experiments/${experimentId}`);
    },
  });

  useEffect(() => {
    if (experiment && organizations) {
      setOrganization(experiment.website.company.id);
      setTimeout(() => setWebsite(experiment.website.id));
    }
  }, [experiment, organizations]);

  /**
   * Throws instead of returning undefined.
   *
   * The guard used to `return` silently, and the caller then read `.id` off the
   * undefined result - so a missing website selection surfaced as a TypeError in the
   * console and a Create dialog that looked as though the click had not landed.
   */
  const createExperiment = async (newExperiment: any) => {
    if (!website) {
      throw new Error('Select a client and a website in the header before creating an experiment.');
    }
    // The owning company comes from the website on the server (PER-263), so only the website is sent.
    const payload = { ...newExperiment, websiteId: website.id };
    const dbExperiment = await apiClient.post('/experiments', payload);
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS'] });
    return dbExperiment;
  };

  const finishSetup = async (id: number, payload: any) => {
    await apiClient.put(`/experiments/${id}/finish-setup`, payload);
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', id] });
  };

  const updateExperiment = async (id: number, payload: any) => {
    await apiClient.put(`/experiments/${id}`, payload);
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', id] });
    // The list shows the name too, so a rename would otherwise linger there until its next refetch.
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS'] });
  };

  /**
   * The `website!` these three used to assert is `organization?.websites?.find(...)`, so it
   * can genuinely be absent. The assertion sat *after* the request, which meant a missing
   * selection threw a TypeError once the experiment had already been deleted - an error on
   * screen for something that had in fact succeeded. Checking first keeps the failure
   * honest and the message actionable.
   */
  const requireWebsite = () => {
    if (!website)
      throw new Error('Select a website in the header first - this action needs to know which site it is for.');
    return website;
  };

  const deleteExperiment = async (id: number) => {
    const { id: websiteId } = requireWebsite();
    await apiClient.delete(`/experiments/${id}`);
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS', websiteId] });
  };

  const archiveExperiment = async (id: number) => {
    const { id: websiteId } = requireWebsite();
    await apiClient.post(`/experiments/${id}/archive`);
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS', websiteId] });
  };

  const startExperiment = async (id: number) => {
    // Was a bare `return`: Start did nothing at all, with no indication why. Uses this
    // file's existing toast rather than sonner so the two reports from one function match.
    if (!website) {
      toast({
        title: 'Cannot start this experiment yet',
        description: 'Select a website in the header first.',
      });
      return;
    }

    setIsStarting(true);
    let step: 'gan' | 'api' = 'api';
    try {
      const { ganMeasurementId, ganProperty } = website;

      if (ganMeasurementId && ganProperty?.hasEditPermission.length > 0) {
        step = 'gan';
        await ganClient.post(`/create-audiences/${id}`);
      }
      // Weights are resolved server-side from the stored variants; the client
      // no longer computes or sends them.
      //
      // No enforceNewRules here any more, either: startExperiment publishes the
      // runtime artifact itself, so calling it again only purges the CDN a second time.
      step = 'api';
      await apiClient.post(`/experiments/${id}/start`, {});
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', id] });
      queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', id] });
      setTimeout(() => setIsStarting(false), 50);
    } catch (error) {
      // 400/409 messages from the API are written to be user-facing (e.g.
      // "Variant weights must sum to 100%") — showing the generic text instead
      // sends the user to support for a problem they could fix themselves.
      const { status, message } = (error ?? {}) as { status?: number; message?: string };
      const isActionable = status === 400 || status === 409;
      toast({
        title: step === 'gan' ? 'Error creating Audience in Google Analytics' : 'Failed to start experiment',
        description: isActionable && message ? message : 'Please try again later or report it to our team',
      });
      console.error(error);
      setTimeout(() => setIsStarting(false), 50);
    }
  };

  const endExperiment = async (id: number) => {
    await apiClient.post(`/experiments/${id}/end`);
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', id] });
  };

  /**
   * QA on (again, which restarts its three days) and off. Both publish server-side, so the
   * preview link works, or stops, without a second request. They throw, like endExperiment,
   * so the caller shows the API's message: a 409 says why (not a draft, not in QA).
   */
  const toggleQa = async (id: number, action: 'start' | 'stop') => {
    await apiClient.post(`/experiments/${id}/qa/${action}`, {});
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT', id] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', id] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS'] });
  };
  const startQa = (id: number) => toggleQa(id, 'start');
  const stopQa = (id: number) => toggleQa(id, 'stop');

  /**
   * `targetWebsiteId` files the copy under another website of the same account. Both the
   * current and the target list are refreshed, so the copy shows up wherever it landed.
   */
  const duplicateExperiment = async (id: number, name: string, targetWebsiteId?: number | null) => {
    const { id: websiteId } = requireWebsite();
    if (!name.trim()) throw new Error('Give the copy a name before duplicating.');
    await apiClient.post(`/experiments/${id}/duplicate`, { name, websiteId: targetWebsiteId ?? undefined });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS', websiteId] });
    if (targetWebsiteId && targetWebsiteId !== websiteId) {
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS', targetWebsiteId] });
    }
  };

  const deployExperiment = async (variantId: number) => {
    try {
      await apiClient.post(`/experiments/${experimentId}/deploy/${variantId}`);
    } finally {
      // A refused deploy (409) means this tab is behind: someone deployed or ended it meanwhile,
      // so the experiment (which carries its activity log) is refetched either way.
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    }
  };

  /**
   * `setEnforcing(false)` is in a `finally` because it was not reached on a failed request,
   * which left the button spinning "Enforcing..." for good with no way back.
   */
  const enforceNewRules = async () => {
    const { id: websiteId } = requireWebsite();
    setEnforcing(true);
    try {
      await apiClient.post(`/websites/${websiteId}/enforce-new-rules`);
    } finally {
      setEnforcing(false);
    }
  };

  return {
    experiment,
    isStarting,
    enforcing,
    enforceNewRules,
    createExperiment,
    finishSetup,
    updateExperiment,
    startExperiment,
    startQa,
    stopQa,
    deleteExperiment,
    archiveExperiment,
    endExperiment,
    deployExperiment,
    duplicateExperiment,
  };
};
